import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Tabs, Tab, Table, Badge, Spinner, Alert, InputGroup } from 'react-bootstrap';
import { 
  FaBook, 
  FaRobot, 
  FaEdit, 
  FaDownload, 
  FaCopy, 
  FaKey, 
  FaSearch, 
  FaFileAlt, 
  FaCheck,
  FaFileCode,
  FaBookOpen,
  FaCogs,
  FaTrash
} from 'react-icons/fa';
// ReactMarkdown and remarkGfm removed to prevent ESM transpilation errors
import rutaManuales from "../../data/rutaManuales";
import nombres from "../../data/nombresPantalla.json";
import VexaCompilationPanel from "./VexaCompilationPanel";

const MODULES = [
  "root",
  "GestionDocumental",
  "GestionOnboarding",
  "GestionUsuarios",
  "GestionAsistencias",
  "GestionAusencias",
  "agentes",
  "catalogos",
  "recruiting"
];

const parseMarkdownToHtml = (markdown) => {
  if (!markdown) return '';
  
  let html = markdown
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  
  // Headers (H3, H4, H5, H6)
  html = html.replace(/^#### (.*?)$/gm, '<h6 class="mt-3 mb-2 fw-bold text-dark">$1</h6>');
  html = html.replace(/^### (.*?)$/gm, '<h5 class="mt-4 mb-2 fw-bold text-dark border-bottom pb-1">$1</h5>');
  html = html.replace(/^## (.*?)$/gm, '<h4 class="mt-4 mb-3 fw-bold text-dark">$1</h4>');
  html = html.replace(/^# (.*?)$/gm, '<h3 class="mt-4 mb-3 fw-bold text-dark">$1</h3>');
  
  // Bold (**text**)
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  // Italics (*text*)
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  
  // Links ([text](url))
  html = html.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="text-primary fw-semibold">$1</a>');
  
  // Code block (```code```)
  html = html.replace(/```([\s\S]*?)```/g, '<pre class="bg-light p-3 border rounded font-monospace my-3" style="font-size: 13px; white-space: pre-wrap; max-height: 350px; overflow: auto; color: #212529;">$1</pre>');
  
  // Inline code (`code`)
  html = html.replace(/`(.*?)`/g, '<code class="bg-light px-1 border rounded font-monospace" style="color: #d63384; font-size: 85%; font-weight: 500;">$1</code>');
  
  // Blockquotes / Alerts
  html = html.replace(/^\s*&gt;\s+\[!IMPORTANT\]\s*(.*?)$/gm, '<div class="alert alert-danger py-2 px-3 my-2 small d-flex align-items-center gap-2"><strong>IMPORTANTE:</strong> $1</div>');
  html = html.replace(/^\s*&gt;\s+\[!WARNING\]\s*(.*?)$/gm, '<div class="alert alert-warning py-2 px-3 my-2 small d-flex align-items-center gap-2"><strong>ADVERTENCIA:</strong> $1</div>');
  html = html.replace(/^\s*&gt;\s+\[!NOTE\]\s*(.*?)$/gm, '<div class="alert alert-info py-2 px-3 my-2 small d-flex align-items-center gap-2"><strong>NOTA:</strong> $1</div>');
  html = html.replace(/^\s*&gt;\s+(.*?)$/gm, '<blockquote class="border-start border-3 ps-3 text-muted" style="font-style: italic; border-color: #6c757d !important;">$1</blockquote>');

  // List items
  html = html.replace(/^\s*-\s+(.*?)$/gm, '<li>$1</li>');
  html = html.replace(/^\s*\d+\.\s+(.*?)$/gm, '<li>$1</li>');

  // Table parser
  const parseTableBlock = (rows) => {
    if (!rows || rows.length < 2) return rows.join('\n');
    // Skip separator row (second row) if it looks like |---|---|
    const isSeparator = (row) => /^\s*\|([\s\-:|]+\|)+\s*$/.test(row);
    let headerRows = [rows[0]];
    let bodyRows = rows.slice(1).filter(r => !isSeparator(r));

    const parseCells = (row) => row
      .trim()
      .replace(/^\|/, '')
      .replace(/\|$/, '')
      .split('|')
      .map(c => c.trim());

    const headers = parseCells(headerRows[0]);
    let tableHtml = '<div class="table-responsive my-3"><table class="table table-bordered table-striped table-sm">';
    tableHtml += '<thead class="table-light"><tr>' + headers.map(h => `<th>${h}</th>`).join('') + '</tr></thead>';
    if (bodyRows.length) {
      tableHtml += '<tbody>' + bodyRows.map(row => {
        const cells = parseCells(row);
        return '<tr>' + cells.map((c, i) => `<td>${c}</td>`).join('') + '</tr>';
      }).join('') + '</tbody>';
    }
    tableHtml += '</table></div>';
    return tableHtml;
  };

  // Handle tables, lists and paragraphs
  const lines = html.split('\n');
  let inList = false;
  let inTable = false;
  let tableRows = [];
  let result = [];

  const flushTable = () => {
    if (tableRows.length) {
      result.push(parseTableBlock(tableRows));
      tableRows = [];
      inTable = false;
    }
  };

  for (let line of lines) {
    const trimmed = line.trim();
    const isTableRow = /^\s*\|.*\|\s*$/.test(trimmed);

    if (isTableRow) {
      if (!inTable) {
        inTable = true;
      }
      tableRows.push(line);
      continue;
    } else {
      flushTable();
    }

    if (trimmed.startsWith('<li>')) {
      if (!inList) {
        result.push('<ul class="my-2" style="padding-left: 20px;">');
        inList = true;
      }
      result.push(line);
    } else {
      if (inList) {
        result.push('</ul>');
        inList = false;
      }

      if (trimmed.startsWith('<h') || trimmed.startsWith('<pre') || trimmed.startsWith('</pre') ||
          trimmed.startsWith('<div') || trimmed.startsWith('</div') || trimmed.startsWith('<blockquote') ||
          trimmed.startsWith('</blockquote') || trimmed === '') {
        result.push(line);
      } else {
        result.push(`<p class="mb-2" style="line-height: 1.6; color: #212529;">${line}</p>`);
      }
    }
  }

  flushTable();
  if (inList) {
    result.push('</ul>');
  }

  return result.join('\n');
};

const DocsManager = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('explorer');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRoute, setSelectedRoute] = useState(null);
  
  // Manual Editor States
  const [manualContent, setManualContent] = useState('');
  const [loadingManual, setLoadingManual] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');

  // Knowledge Base States
  const [viewMode, setViewMode] = useState('manual');
  const [knowledgeContent, setKnowledgeContent] = useState('');
  const [loadingKnowledge, setLoadingKnowledge] = useState(false);

  // AI Generator States
  const [selectedModule, setSelectedModule] = useState('root');
  const [componentName, setComponentName] = useState('');
  const [sourceCode, setSourceCode] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generatedManual, setGeneratedManual] = useState('');
  const [apiKey, setApiKey] = useState(localStorage.getItem('deepseek_api_key_override') || '');
  const [selectedModel, setSelectedModel] = useState('deepseek-chat');

  // Vexa Compilation States
  const [compilationStatus, setCompilationStatus] = useState(null);
  const [compiling, setCompiling] = useState(false);

  // Load selected manual and knowledge base
  useEffect(() => {
    if (!selectedRoute) return;

    const loadFiles = async () => {
      setLoadingManual(true);
      setLoadingKnowledge(true);
      setSaveStatus('');
      setIsEditing(false);
      setViewMode('manual');

      const entry = rutaManuales[selectedRoute];
      if (!entry || !entry.archivo) {
        setManualContent('> No hay archivo de manual configurado para esta ruta.');
        setKnowledgeContent('');
        setLoadingManual(false);
        setLoadingKnowledge(false);
        return;
      }

      const archivoSinExt = entry.archivo.replace(/\.(js|jsx)$/i, "");
      const basePath = `/manuales/${entry.modulo}/${archivoSinExt}`;

      try {
        const mdResponse = await fetch(`${basePath}.md`);
        if (mdResponse.ok) {
          setManualContent(await mdResponse.text());
        } else {
          setManualContent(`### ¿Para qué sirve esta pantalla?\n\n[Manual no creado aún para ${entry.archivo}]\n\nHaga clic en "Generador con IA" o edite este texto para crear el manual.`);
        }
      } catch (error) {
        console.error("Error al cargar el manual:", error);
        setManualContent('> Error al cargar el archivo de manual.');
      } finally {
        setLoadingManual(false);
      }

      try {
        const jsonResponse = await fetch(`${basePath}.json`);
        if (jsonResponse.ok) {
          setKnowledgeContent(await jsonResponse.text());
        } else {
          setKnowledgeContent('// Base de conocimiento no creada aún para este componente.\n// Ejecute: node scripts/generateManuals.js para generarla.');
        }
      } catch {
        setKnowledgeContent('// Error al cargar la base de conocimiento.');
      } finally {
        setLoadingKnowledge(false);
      }
    };

    loadFiles();
  }, [selectedRoute]);

  // Handle File Upload for Code Parser
  const handleCodeUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Set component name automatically
    setComponentName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      setSourceCode(e.target.result);
    };
    reader.readAsText(file);
  };

  // Generate Manual using DeepSeek API
  const handleGenerateManual = async () => {
    const activeKey = apiKey || process.env.REACT_APP_DEEPSEEK_API_KEY || process.env.DEEPSEEK_API_KEY;
    if (!activeKey) {
      alert("Por favor ingrese una API Key de DeepSeek en la pestaña de configuración o agregue DEEPSEEK_API_KEY a su archivo .env.");
      return;
    }

    if (!sourceCode.trim()) {
      alert("Por favor cargue un archivo o pegue el código fuente del componente.");
      return;
    }

    if (!componentName.trim()) {
      alert("Por favor asigne un nombre de componente (ej: PDFSignatureDetector.js).");
      return;
    }

    // Buscar la ruta asociada
    const routeEntry = Object.entries(rutaManuales).find(([r, entry]) => entry && entry.archivo === componentName);
    const urlPath = routeEntry ? routeEntry[0] : "";

    const NOMBRES_MODULOS = {
      "GestionUsuarios": "Gestión de Usuarios",
      "GestionPermisosRoles": "Permisos y Roles",
      "GestionDocumental": "Gestión de Documentos",
      "GestionOnboarding": "Gestión de Onboarding",
      "GestionAsistencias": "Gestión de Asistencias",
      "GestionAusencias": "Gestión de Ausencias",
      "GestionEPP": "Gestión de EPP y Activos",
      "GestionContable": "Gestión Contable",
      "planillas": "Planillas de Remuneraciones",
      "root": "General",
      "recruiting": "Reclutamiento y Selección",
      "catalogos": "Catálogos",
      "empleados": "Empleados"
    };

    const moduloReadable = NOMBRES_MODULOS[selectedModule] || selectedModule;
    const pantallaReadable = nombres[componentName] || componentName.replace(/\.(js|jsx)$/i, "");

    setGenerating(true);
    setGeneratedManual('');
    
    try {
      const SYSTEM_PROMPT = `Eres un capacitador experto en software de RRHH.
Generas manuales técnicos detallados para administradores de empresas que usan Neurum OS.
Usas lenguaje claro y preciso. Incluyes tablas cuando sea necesario.
Siempre respondes en MARKDOWN válido, en español.`;

      const prompt = `Genera un manual de usuario para la siguiente pantalla de Neurum OS.

## DATOS DEL COMPONENTE
- Nombre de la pantalla: ${componentName.replace(/\.js$/i, "")}
- Módulo: ${selectedModule}
- Módulo legible: ${moduloReadable}
- Pantalla legible: ${pantallaReadable}
- Ruta: ${moduloReadable} -> ${pantallaReadable}
- Enlace directo: ${urlPath || '/'}

## ESTRUCTURA EXIGIDA:

Usa exactamente estos encabezados ### en este orden. NO incluyas título principal, el sistema lo agrega:

### Descripción de la pantalla
Explica qué hace esta pantalla, su propósito principal y qué administra.

### ¿Quién puede usar esta pantalla?
Roles y permisos necesarios para acceder.

### Antes de comenzar
Requisitos previos, configuraciones necesarias antes de usar la pantalla.

### Flujo general del proceso
Pasos numerados del flujo principal de principio a fin.

### Campos principales (tabla: Campo|Descripción|Obligatorio|Tipo|Observaciones)
Tabla markdown con los campos del formulario o tabla principal.

### Acciones disponibles (tabla: Acción|Descripción|Permiso|Riesgo|Impacto)
Tabla markdown con cada acción disponible, su descripción, permiso requerido, nivel de riesgo e impacto.

### Procedimientos
Para cada acción principal, crea una subsección #### con este formato:
#### #### Nombre de la acción
- **Objetivo**: ...
- **Cuándo usar**: ...
- **Requisitos**: ...
- **Tiempo estimado**: ...
- **Nivel de dificultad**: ...
- **Pasos**: 1. ... 2. ... 3. ...
- **Resultado esperado**: ...
- **Validaciones**: ...
- **Impacto**: ...
- **Errores frecuentes**: ...
- **Notas**: ...

### Verificación posterior
Cómo verificar que los cambios se aplicaron correctamente.

### Casos de uso frecuentes
Lista numerada con casos típicos: **Título**: Descripción del caso.

### Preguntas frecuentes
Subsecciones #### con cada pregunta y su respuesta.

### Errores comunes (tabla: Mensaje|Causa|Solución|Gravedad)
Tabla markdown con mensajes de error, causa, solución y nivel de gravedad.

### Buenas prácticas
Recomendaciones para el usuario.

### Advertencias importantes (solo irreversibles, con ⚠)
Advertencias sobre acciones irreversibles, marcadas con ⚠.

### Permisos necesarios (tabla: Permiso|Acción|Quién lo posee)
Tabla markdown con permisos, qué acción permiten y quién los posee.

### Ruta de acceso
Texto con la ruta formateada:
**${moduloReadable} → ${pantallaReadable}**
Enlace directo: [${urlPath || '/'}](${urlPath || '/'})

### Resumen funcional
Párrafo final que resume todo el componente.

---

---

## CÓDIGO FUENTE DE LA PANTALLA (usa esto como referencia para generar el manual):
\`\`\`jsx
${sourceCode}
\`\`\`

Genera SOLO el markdown del manual, sin introducciones ni despedidas.`;

      const endpoint = process.env.REACT_APP_DEEPSEEK_GENERATION_ENDPOINT || 
                       process.env.DEEPSEEK_GENERATION_ENDPOINT || 
                       "https://api.deepseek.com/v1/chat/completions";

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${activeKey}`,
        },
        body: JSON.stringify({
          model: selectedModel,
          messages: [
            { role: "system", content: SYSTEM_PROMPT },
            { role: "user", content: prompt },
          ],
          temperature: 0.2,
          max_tokens: 4000,
        }),
      });

      if (!response.ok) {
        const err = await response.text();
        throw new Error(`Error de HTTP ${response.status}: ${err}`);
      }

      const data = await response.json();
      const text = data?.choices?.[0]?.message?.content || "";
      const cleanedText = text.trim().replace(/^```markdown/i, '').replace(/^```/g, '').replace(/```$/g, '').trim();

      setGeneratedManual(cleanedText);
      setManualContent(cleanedText);
      
      alert("Manual generado con éxito por DeepSeek. Puede revisarlo y descargarlo.");
    } catch (error) {
      console.error("Error al generar manual con DeepSeek:", error);
      alert(`Error al generar manual: ${error.message}`);
    } finally {
      setGenerating(false);
    }
  };

  // Copy manual to clipboard
  const handleCopyToClipboard = (content) => {
    navigator.clipboard.writeText(content);
    alert("¡Manual copiado al portapapeles con éxito!");
  };

  // Download manual as markdown file
  const handleDownloadManual = (fileName, content) => {
    const blob = new Blob([content], { type: 'text/markdown;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    
    const cleanFileName = fileName.replace(/\.(js|jsx)$/i, "") + ".md";
    link.setAttribute("download", cleanFileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setSaveStatus('¡Archivo descargado! Recuerde guardarlo en docs/manuales y public/manuales.');
  };

  // Save to Firestore and trigger Vexa compilation + manifest ingestion
  const handleSaveAndCompile = async () => {
    if (!selectedRoute || !manualContent) return;
    const entry = rutaManuales[selectedRoute];
    const archivoSinExt = entry.archivo.replace(/\.(js|jsx)$/i, "");

    setCompiling(true);
    setSaveStatus('Guardando archivos locales...');

    try {
      // Save files to local disk via save server
      try {
        await fetch('http://localhost:5678', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            modulo: entry.modulo,
            archivo: entry.archivo,
            mdContent: manualContent,
            jsonContent: knowledgeContent || '',
          }),
        });
        setSaveStatus('✅ Archivos guardados en docs/manuales/');
      } catch (saveErr) {
        console.warn('Save server not running, skipping local save:', saveErr.message);
        setSaveStatus('Generando manifest de ingesta...');
      }

      const {
        buildManifest,
        ingestManifest,
        downloadManifest,
      } = await import('../../services/vexaIngestionService');

      const manifest = buildManifest({
        modulo: entry.modulo,
        componente: archivoSinExt,
        archivo: entry.archivo,
        manualContent,
        knowledgeContent,
        version: '1.0.0',
      });

      // Download manifest for inspection
      downloadManifest(manifest, `manifest_${entry.modulo}_${archivoSinExt}.json`);

      setSaveStatus(`Enviando ${manifest.total_chunks} chunks a VEXA...`);
      const result = await ingestManifest(manifest);

      setCompilationStatus({
        status: result.status || 'READY',
        chunksCount: result.chunksCount,
        embeddingsCount: result.embeddingsCount,
      });

      setSaveStatus(`✅ Ingesta exitosa: ${result.chunksCount} chunks, ${result.embeddingsCount} embeddings`);
    } catch (error) {
      console.error('Error al guardar y compilar:', error);
      setSaveStatus(`❌ Error: ${error.message}`);
      setCompilationStatus({ status: 'FAILED' });
    } finally {
      setCompiling(false);
    }
  };

  // Delete manual chunks from Vexa
  const handleDeleteManual = async () => {
    if (!selectedRoute) return;
    const entry = rutaManuales[selectedRoute];
    const archivoSinExt = entry.archivo.replace(/\.(js|jsx)$/i, "");
    const manualId = `${entry.modulo}_${archivoSinExt}`;

    if (!window.confirm(`¿Eliminar los chunks y embeddings de "${nombres[entry.archivo] || archivoSinExt}" de Vexa?`)) return;

    setCompiling(true);
    setSaveStatus(`Eliminando ${manualId}...`);
    try {
      const { vexaCompilationService } = await import('../../services/vexaCompilationService');
      await vexaCompilationService.deleteManual(manualId);
      setSaveStatus(`🗑️ Manual "${nombres[entry.archivo] || archivoSinExt}" eliminado de Vexa.`);
    } catch (error) {
      console.error('Error al eliminar:', error);
      setSaveStatus(`❌ Error: ${error.message}`);
    } finally {
      setCompiling(false);
    }
  };

  // Save API Key Override to LocalStorage
  const handleSaveApiKey = (key) => {
    setApiKey(key);
    localStorage.setItem('gemini_api_key_override', key);
    alert("API Key de Gemini guardada localmente.");
  };

  // Filter routes based on query
  const filteredRoutes = Object.keys(rutaManuales).filter(route => {
    const data = rutaManuales[route];
    if (!data.archivo) return false;
    
    const friendlyName = nombres[data.archivo] || '';
    
    return (
      route.toLowerCase().includes(searchQuery.toLowerCase()) ||
      data.archivo.toLowerCase().includes(searchQuery.toLowerCase()) ||
      friendlyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (data.modulo && data.modulo.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  });

  return (
    <Container fluid className="py-4 px-lg-4">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4 p-3 bg-dark text-white rounded shadow-sm">
        <div>
          <h3 className="mb-1" style={{ fontWeight: '600', letterSpacing: '-0.5px' }}>
            <FaBook className="me-2 text-info" /> Gestor Gráfico de Manuales
          </h3>
          <p className="mb-0 text-muted" style={{ fontSize: '13px' }}>
            Herramienta de desarrollo para crear, editar y generar manuales de usuario final con IA.
          </p>
        </div>
      </div>

      <Tabs 
        activeKey={activeTab} 
        onSelect={(k) => setActiveTab(k)} 
        className="mb-4 custom-tabs"
      >
        {/* TAB 1: EXPLORER AND EDITOR */}
        <Tab eventKey="explorer" title={<span><FaBookOpen className="me-1" /> Explorador de Manuales</span>}>
          <Row className="g-4">
            
            {/* Sidebar list of mapped components */}
            <Col lg={4}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Header className="bg-light py-3">
                  <Form.Group className="mb-0">
                    <InputGroup size="sm">
                      <InputGroup.Text className="bg-white border-end-0 text-muted">
                        <FaSearch size={12} />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Buscar componente o ruta..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="border-start-0 ps-0"
                      />
                    </InputGroup>
                  </Form.Group>
                </Card.Header>
                <Card.Body className="p-0" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                  <Table hover responsive striped className="mb-0 align-middle small">
                    <thead className="table-dark sticky-top">
                      <tr>
                        <th>Pantalla / Archivo</th>
                        <th>Módulo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRoutes.map((route) => {
                        const data = rutaManuales[route];
                        const isSelected = selectedRoute === route;
                        return (
                          <tr 
                            key={route} 
                            onClick={() => setSelectedRoute(route)}
                            style={{ cursor: 'pointer' }}
                            className={isSelected ? 'table-info fw-semibold' : ''}
                          >
                            <td>
                              <div className="fw-semibold text-dark">
                                {nombres[data.archivo] || data.archivo.replace(/\.(js|jsx)$/i, "")}
                              </div>
                              <div className="text-muted font-monospace" style={{ fontSize: '11px', wordBreak: 'break-all' }}>
                                {data.archivo}
                              </div>
                              <small className="text-muted d-block mt-1" style={{ fontSize: '10px' }}>
                                Ruta: {route || '/'}
                              </small>
                            </td>
                            <td>
                              <Badge bg={data.modulo === 'root' ? 'secondary' : 'primary'}>
                                {data.modulo || 'root'}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>

            {/* Manual Display / Workspace */}
            <Col lg={8}>
              {selectedRoute ? (
                <Card className="shadow-sm border-0 h-100">
                  <Card.Header className="bg-light py-3 d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <div>
                      <h5 className="mb-0 text-dark fw-bold">
                        {nombres[rutaManuales[selectedRoute].archivo] || rutaManuales[selectedRoute].archivo.replace(/\.(js|jsx)$/i, "")}
                      </h5>
                      <small className="text-muted">
                        Modulo: <strong>{rutaManuales[selectedRoute].modulo || 'root'}</strong> | Ruta: <strong>{selectedRoute || '/'}</strong>
                      </small>
                    </div>

                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <div className="btn-group btn-group-sm me-2" role="group">
                        <Button
                          size="sm"
                          variant={viewMode === 'manual' ? 'primary' : 'outline-primary'}
                          onClick={() => { setViewMode('manual'); setIsEditing(false); }}
                        >
                          <FaBook className="me-1" /> Manual
                        </Button>
                        <Button
                          size="sm"
                          variant={viewMode === 'knowledge' ? 'primary' : 'outline-primary'}
                          onClick={() => { setViewMode('knowledge'); setIsEditing(false); }}
                        >
                          <FaRobot className="me-1" /> Base Conocimiento
                        </Button>
                      </div>

                      <Button 
                        size="sm" 
                        variant={isEditing ? "success" : "outline-primary"}
                        onClick={() => setIsEditing(!isEditing)}
                      >
                        <FaEdit className="me-1" /> {isEditing ? "Ver Vista Previa" : "Editar"}
                      </Button>
                      
                      {viewMode === 'manual' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => handleCopyToClipboard(manualContent)}
                            disabled={!manualContent}
                            title="Copiar Markdown"
                          >
                            <FaCopy />
                          </Button>
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => handleDownloadManual(rutaManuales[selectedRoute].archivo, manualContent)}
                            disabled={!manualContent}
                          >
                            <FaDownload className="me-1" /> .md
                          </Button>
                        </>
                      )}
                      {viewMode === 'knowledge' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => handleCopyToClipboard(knowledgeContent)}
                            disabled={!knowledgeContent}
                            title="Copiar JSON"
                          >
                            <FaCopy />
                          </Button>
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => {
                              const blob = new Blob([knowledgeContent], { type: 'application/json;charset=utf-8;' });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement("a");
                              a.href = url;
                              a.download = rutaManuales[selectedRoute].archivo.replace(/\.(js|jsx)$/i, "") + ".json";
                              a.click();
                              URL.revokeObjectURL(url);
                            }}
                            disabled={!knowledgeContent}
                          >
                            <FaDownload className="me-1" /> .json
                          </Button>
                        </>
                      )}

                      <Button
                        size="sm"
                        variant="success"
                        onClick={handleSaveAndCompile}
                        disabled={compiling || !manualContent}
                      >
                        {compiling ? (
                          <Spinner size="sm" />
                        ) : (
                          <FaCheck className="me-1" />
                        )}
                        {compiling ? 'Compilando...' : 'Guardar y Compilar'}
                      </Button>

                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={handleDeleteManual}
                        disabled={compiling || !selectedRoute}
                        title="Eliminar chunks de Vexa"
                      >
                        <FaTrash className="me-1" /> Eliminar
                      </Button>
                    </div>
                  </Card.Header>

                  <Card.Body className="d-flex flex-column" style={{ minHeight: '500px' }}>
                    {loadingManual || loadingKnowledge ? (
                      <div className="text-center my-auto">
                        <Spinner animation="border" variant="primary" />
                        <p className="text-muted mt-2">Cargando archivos...</p>
                      </div>
                    ) : (
                      <>
                        {saveStatus && (
                          <Alert variant="info" className="py-2 px-3 mb-3 small d-flex align-items-center gap-2">
                            <FaCheck className="text-success" />
                            <span>{saveStatus}</span>
                          </Alert>
                        )}

                        <div className="alert alert-warning py-2 px-3 mb-3 small">
                          <strong>Guía del Servidor:</strong> Para guardar permanentemente, reemplace los archivos en su IDE:
                          <div className="mt-1 font-monospace" style={{ fontSize: '11px' }}>
                            {viewMode === 'knowledge' ? (
                              <>- docs/manuales/{rutaManuales[selectedRoute].modulo}/{rutaManuales[selectedRoute].archivo.replace(/\.(js|jsx)$/i, "")}.json</>
                            ) : (
                              <>- docs/manuales/{rutaManuales[selectedRoute].modulo}/{rutaManuales[selectedRoute].archivo.replace(/\.(js|jsx)$/i, "")}.md</>
                            )}
                          </div>
                        </div>

                        {viewMode === 'knowledge' ? (
                          isEditing ? (
                            <Form.Group className="flex-grow-1 d-flex flex-column">
                              <Form.Label className="fw-semibold text-muted small">Editor JSON (Base de Conocimiento Vexa)</Form.Label>
                              <Form.Control
                                as="textarea"
                                value={knowledgeContent}
                                onChange={(e) => setKnowledgeContent(e.target.value)}
                                className="font-monospace flex-grow-1 p-3"
                                style={{ 
                                  minHeight: '400px', 
                                  fontSize: '12px', 
                                  lineHeight: '1.4',
                                  backgroundColor: '#1e1e1e',
                                  color: '#d4d4d4'
                                }}
                              />
                            </Form.Group>
                          ) : (
                            <div 
                              className="p-4 border rounded bg-white flex-grow-1 overflow-auto" 
                              style={{ maxHeight: '60vh', fontSize: '13px', lineHeight: '1.6' }}
                            >
                              <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {knowledgeContent ? (
                                  <code>{knowledgeContent}</code>
                                ) : (
                                  <span className="text-muted">// Base de conocimiento no disponible</span>
                                )}
                              </pre>
                            </div>
                          )
                        ) : isEditing ? (
                          <Form.Group className="flex-grow-1 d-flex flex-column">
                            <Form.Label className="fw-semibold text-muted small">Editor Markdown</Form.Label>
                            <Form.Control
                              as="textarea"
                              value={manualContent}
                              onChange={(e) => setManualContent(e.target.value)}
                              className="font-monospace flex-grow-1 p-3"
                              style={{ 
                                minHeight: '400px', 
                                fontSize: '13px', 
                                lineHeight: '1.5',
                                backgroundColor: '#f8f9fa' 
                              }}
                            />
                          </Form.Group>
                        ) : (
                          <div 
                            className="markdown-preview p-4 border rounded bg-white flex-grow-1 overflow-auto" 
                            style={{ maxHeight: '60vh', fontSize: '14px', lineHeight: '1.6' }}
                          >
                            <div 
                              className="markdown-preview-content" 
                              style={{ color: '#212529' }}
                              onClick={(e) => {
                                const target = e.target.closest("a");
                                if (target) {
                                  const href = target.getAttribute("href");
                                  if (href && href.startsWith("/")) {
                                    e.preventDefault();
                                    navigate(href);
                                  }
                                }
                              }}
                              dangerouslySetInnerHTML={{ __html: parseMarkdownToHtml(manualContent) }}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </Card.Body>
                </Card>
              ) : (
                <Card className="shadow-sm border-0 h-100 py-5">
                  <Card.Body className="text-center my-auto text-muted">
                    <FaFileAlt size={48} className="mb-3 opacity-25" />
                    <h5>Seleccione una Pantalla</h5>
                    <p className="small mb-0">Seleccione un componente del listado de la izquierda para ver, editar o descargar su manual.</p>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        </Tab>

        {/* TAB 2: AI GENERATOR */}
        <Tab eventKey="generator" title={<span><FaRobot className="me-1" /> Generador con Inteligencia Artificial</span>}>
          <Row className="g-4">
            
            {/* Input parameters panel */}
            <Col lg={5}>
              <Card className="shadow-sm border-0">
                <Card.Header className="bg-light py-3">
                  <h5 className="mb-0 text-dark fw-bold">Generador Automático</h5>
                </Card.Header>
                <Card.Body className="d-flex flex-column gap-3">
                  <Form.Group>
                    <Form.Label className="fw-semibold small text-muted">Módulo del Componente</Form.Label>
                    <Form.Select
                      value={selectedModule}
                      onChange={(e) => setSelectedModule(e.target.value)}
                      style={{ padding: '10px' }}
                    >
                      {MODULES.map(mod => (
                        <option key={mod} value={mod}>{mod}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group>
                    <Form.Label className="fw-semibold small text-muted">Nombre del Componente (.js)</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Ejemplo: PDFSignatureDetector.js"
                      value={componentName}
                      onChange={(e) => setComponentName(e.target.value)}
                      style={{ padding: '10px' }}
                    />
                  </Form.Group>

                  <Form.Group>
                    <Form.Label className="fw-semibold small text-muted">Cargar Código Fuente desde Archivo</Form.Label>
                    <div className="upload-container text-center border border-dashed rounded p-3 bg-light" style={{ cursor: 'pointer', position: 'relative' }}>
                      <input
                        type="file"
                        accept=".js,.jsx"
                        onChange={handleCodeUpload}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          opacity: 0,
                          cursor: 'pointer'
                        }}
                      />
                      <FaFileCode size={24} className="text-primary mb-2" />
                      <p className="mb-0 fw-semibold small text-dark">
                        Seleccionar archivo de código fuente (.js, .jsx)
                      </p>
                      <small className="text-muted" style={{ fontSize: '11px' }}>
                        Se leerá el archivo directamente en el navegador
                      </small>
                    </div>
                  </Form.Group>

                  <Form.Group>
                    <Form.Label className="fw-semibold small text-muted">O Pegar Código Fuente Aquí</Form.Label>
                    <Form.Control
                      as="textarea"
                      placeholder="Pegue el código fuente de su vista React aquí..."
                      value={sourceCode}
                      onChange={(e) => setSourceCode(e.target.value)}
                      className="font-monospace"
                      style={{ minHeight: '150px', fontSize: '12px' }}
                    />
                  </Form.Group>

                  <div className="d-grid mt-2">
                    <Button 
                      variant="dark"
                      className="py-2"
                      onClick={handleGenerateManual}
                      disabled={generating}
                      style={{ fontWeight: '500' }}
                    >
                      {generating ? (
                        <><Spinner animation="border" size="sm" className="me-2" /> Generando con DeepSeek...</>
                      ) : (
                        <><FaRobot className="me-2" /> Generar Manual con DeepSeek</>
                      )}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Manual Output Panel */}
            <Col lg={7}>
              {generatedManual ? (
                <Card className="shadow-sm border-0 h-100">
                  <Card.Header className="bg-light py-3 d-flex align-items-center justify-content-between">
                    <h5 className="mb-0 text-dark fw-bold">Manual Generado por IA</h5>
                    <div className="d-flex gap-2">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => handleCopyToClipboard(generatedManual)}
                      >
                        <FaCopy className="me-1" /> Copiar
                      </Button>
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => handleDownloadManual(componentName, generatedManual)}
                      >
                        <FaDownload className="me-1" /> Descargar (.md)
                      </Button>
                    </div>
                  </Card.Header>
                  <Card.Body className="d-flex flex-column">
                    <Alert variant="success" className="py-2 px-3 mb-3 small d-flex align-items-center gap-2">
                      <FaCheck />
                      <span>El manual fue generado con éxito por {selectedModel}. Revíselo abajo.</span>
                    </Alert>

                    <div 
                      className="p-4 border rounded bg-white flex-grow-1 overflow-auto" 
                      style={{ maxHeight: '60vh', fontSize: '14px', lineHeight: '1.6' }}
                    >
                      <div 
                        className="markdown-preview-content" 
                        style={{ color: '#212529' }}
                        dangerouslySetInnerHTML={{ __html: parseMarkdownToHtml(generatedManual) }}
                      />
                    </div>
                  </Card.Body>
                </Card>
              ) : (
                <Card className="shadow-sm border-0 h-100 py-5">
                  <Card.Body className="text-center my-auto text-muted">
                    <FaRobot size={48} className="mb-3 opacity-25" />
                    <h5>Esperando Código Fuente</h5>
                    <p className="small mb-0">Cargue o pegue el código fuente del componente a la izquierda y presione "Generar" para redactar el manual automáticamente.</p>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        </Tab>

        {/* TAB 3: API SETTINGS */}
        <Tab eventKey="settings" title={<span><FaCogs className="me-1" /> Configuración</span>}>
          <Card className="shadow-sm border-0 w-75 mx-auto">
            <Card.Header className="bg-dark text-white py-3">
              <h5 className="mb-0 fw-bold"><FaKey className="me-2 text-warning" /> Configuración de Inteligencia Artificial</h5>
            </Card.Header>
            <Card.Body className="p-4 d-flex flex-column gap-3">
              <Form.Group>
                <Form.Label className="fw-semibold text-muted">API Key de DeepSeek</Form.Label>
                <Form.Control
                  type="password"
                  disabled={!!(process.env.REACT_APP_DEEPSEEK_API_KEY || process.env.DEEPSEEK_API_KEY)}
                  placeholder={(process.env.REACT_APP_DEEPSEEK_API_KEY || process.env.DEEPSEEK_API_KEY) ? "Bloqueado: Detectado y cargado desde tu archivo .env" : "Pegue su API Key aquí"}
                  value={(process.env.REACT_APP_DEEPSEEK_API_KEY || process.env.DEEPSEEK_API_KEY) ? "********************" : apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  style={{ padding: '10px' }}
                />
                <Form.Text className="text-muted small d-block mt-2">
                  {(process.env.REACT_APP_DEEPSEEK_API_KEY || process.env.DEEPSEEK_API_KEY) ? (
                    <span className="text-success fw-semibold">
                      ✔ ¡Seguridad activa! El sistema está utilizando la API Key declarada de forma privada en tu archivo `.env`. El campo en el navegador ha sido bloqueado y no almacena nada en localStorage.
                    </span>
                  ) : (
                    <span>
                      <strong>Recomendación de Seguridad:</strong> Para evitar guardar claves en el navegador, agrégala en tu archivo <code>.env</code> en la raíz del proyecto como <code>REACT_APP_DEEPSEEK_API_KEY=tu_clave</code> y reinicia tu servidor de desarrollo. Si la pegas aquí, se guardará solo en el <code>localStorage</code> de tu máquina local.
                    </span>
                  )}
                </Form.Text>
              </Form.Group>

              <Form.Group>
                <Form.Label className="fw-semibold text-muted">Modelo de Inteligencia Artificial</Form.Label>
                <Form.Select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  style={{ padding: '10px' }}
                >
                  <option value="deepseek-chat">DeepSeek Chat (V3 / V4 Flash - Recomendado)</option>
                  <option value="deepseek-coder">DeepSeek Coder</option>
                </Form.Select>
              </Form.Group>

              <hr className="my-4" />

              <Form.Group>
                <Form.Label className="fw-semibold text-muted">Mapeo de Rutas de Manuales</Form.Label>
                <div className="p-3 bg-light rounded border">
                  <p className="small text-dark mb-2">
                    Si agregaste nuevas pantallas en <code>Inicio.js</code>, debes regenerar el archivo de mapeo <code>rutaManuales.js</code> para que aparezcan en el explorador.
                  </p>
                  <p className="small text-muted mb-3">
                    Dado que el navegador no puede modificar tus archivos locales por seguridad, ejecuta el siguiente comando en tu terminal de desarrollo para actualizar las rutas:
                  </p>
                  <div className="d-flex align-items-center justify-content-between gap-2 p-2 bg-dark rounded font-monospace text-white mb-2" style={{ fontSize: '12px' }}>
                    <span className="flex-grow-1 select-all">node scripts/generarRutaManuales.js</span>
                    <Button 
                      size="sm" 
                      variant="outline-info" 
                      onClick={() => {
                        navigator.clipboard.writeText("node scripts/generarRutaManuales.js");
                        alert("¡Comando copiado al portapapeles!");
                      }}
                    >
                      <FaCopy size={11} /> Copiar
                    </Button>
                  </div>
                </div>
              </Form.Group>

              <div className="d-flex gap-2 justify-content-end mt-4">
                <Button 
                  variant="outline-secondary"
                  onClick={() => {
                    setApiKey('');
                    localStorage.removeItem('deepseek_api_key_override');
                    alert('Clave de localStorage removida.');
                  }}
                >
                  Limpiar Configuración
                </Button>
                <Button 
                  variant="primary"
                  onClick={() => handleSaveApiKey(apiKey)}
                >
                  Guardar Cambios
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="compilation" title={<span><FaCogs className="me-1" /> Compilación</span>}>
          <Card className="shadow-sm border-0 mt-3">
            <Card.Body>
              {selectedRoute ? (
                <VexaCompilationPanel
                  manualId={`${rutaManuales[selectedRoute]?.modulo || 'root'}_${(rutaManuales[selectedRoute]?.archivo || '').replace(/\.(js|jsx)$/i, '')}`}
                  module={rutaManuales[selectedRoute]?.modulo || 'root'}
                  screen={(rutaManuales[selectedRoute]?.archivo || '').replace(/\.(js|jsx)$/i, '')}
                  content={manualContent}
                />
              ) : (
                <p className="text-muted text-center my-4">
                  Selecciona un manual en el explorador para ver su estado de compilación.
                </p>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </Container>
  );
};

export default DocsManager;
